'use strict'

const ps = require('pubsub-js');

// Holds state set in setValue and retrieved by getValue
let state = {};

/**
 * This returns a function that will be passed to pubsub-js. It is needed because
 * the former system did not pass the eventId as the first parameter. This intercepts
 * and passes the value back in the first parameter.
 *
 * @param {function} _fn function to be called when a value is published
 * @return {function}
 */
const getPsFn = (_fn) => {
    const fn = _fn;

    return (eventId, value) => {
        fn(value);
    }
}

/**
 * Places function fn as a listener on values published to eventId
 *
 * @param {string} eventId
 * @param {function} fn
 */
const subscribe = (eventId, fn) => {
    const psFn = getPsFn(fn);
    const psToken = ps.subscribe(eventId, psFn);
    fn.psToken = psToken;
};

/**
 * Parses out the value of value to match formats used with previous version
 * of nuskins pub/sub functionality.
 *
 * @param {*} value
 * @return {*}
 */
const parseValue = (value) => {
    return Array.isArray(value) && value.length > 0
        ? value[0]
        : value === null || value === undefined
            ? true
            : value
}

/**
 * Publishes value to eventId listeners.
 *
 * @param {string} eventId
 * @param {*} value
 */
const publish = (eventId, value) => {
    const val = parseValue(value);
    ps.publishSync(eventId, val);

    // Push the event out to GTM if the dataLayer is defined
    try {
        if (window && typeof window.dataLayer !== "undefined") {
            let gtmObj = {
                event: eventId
            };

            if (val) {
                if (Array.isArray(val)) {
                    // 2 is the configuration object
                    val.map((obj) => {
                        gtmObj = Object.assign(gtmObj, obj);
                    });
                } else if (typeof val === "object") {
                    gtmObj = Object.assign(gtmObj, val); // contains the payload object
                }
            }
            window.dataLayer.push(gtmObj);
        }
    } catch (err) {
        console.log('Error pushing event to GTM', err);
    }
};

/**
 * unsubscribes fn from listening to eventId
 *
 * @param {string} eventId
 * @param {function} fn
 */
const unsubscribe = (eventId, fn) => {
    if (typeof eventId === 'string') {
        ps.unsubscribe(typeof fn === 'function' ? fn.psToken : eventId);
    }
};


/**
 * Gets value for eventId from state and passes it into fn and also returns it.
 *
 * @param {string} eventId
 * @param {function} fn
 * @return {*}
 */
const _getValue = (eventId, fn) => {
    if (typeof fn === 'function') {
        fn(state[eventId]);
    }

    return state[eventId];
};

/**
 * Gets the value set on eventId in state. Optionally subcribes to eventId
 *
 * @param {string} eventId
 * @param {function} fn
 * @param {boolean} doSubscribe
 * @return {*}
 */
const getValue = (eventId, fn, doSubscribe = false) => {
    if (doSubscribe === true) {
        subscribe(eventId, fn);
    }
    const rv = _getValue(eventId, fn);

    return rv;
};

/**
 * Sets a value for an eventId, also publishes the value.
 *
 * @param {string} eventId
 * @param {*} value
 */
const setValue = (eventId, value) => {
    if (typeof eventId === 'string' && eventId.length > 0) {
        state[eventId] = parseValue(value);
        publish(eventId, value);
    }
};

/**
 * Clears all subscriptions for an eventId. Unit test helper.
 *
 * @param {string} eventId
 */
const clearAllSubscriptions = (eventId) => {
    ps.clearAllSubscriptions(eventId);
};

/**
 * Returns the number of subscriptions for an event Id. This is here
 * mainly for unit tests.
 *
 * @param {string} eventId
 * @return {number}
 */
const countSubscriptions = (eventId) => {
    return ps.countSubscriptions(eventId);
};

/**
 * Helper for testing. Not functionality brought over from nuskinjquery
 * This clears state.
 */
const clearState = () => {
    state = {};
};

module.exports = {
    subscribe,
    publish,
    unsubscribe,
    getValue,
    setValue,
    clearAllSubscriptions,
    countSubscriptions,
    clearState
};
